// In-app dialogs and toasts. Dialogs are only for decisions with consequences
// (release, list, fuse, reset); they never pause the hunt, which runs on the
// server whatever is open. Esc or a click on the scrim cancels, Enter confirms
// unless the action is destructive, and focus stays inside while it's open.
import { esc } from './fmt.ts';
import { fitLists, setFitPage } from './fitlist.ts';

export interface DialogAction { label: string; value: string; kind?: 'primary' | 'danger' | 'gold'; disabled?: boolean }
export interface DialogOpts {
  title: string;
  /** HTML. Inputs with a name attribute come back in `data`. */
  body: string;
  actions: DialogAction[];
  /** Destructive dialogs don't confirm on Enter. */
  danger?: boolean;
  /** Called on every input inside the dialog, to refresh previews and buttons. */
  onInput?: (root: HTMLElement, data: Record<string, string>) => void;
}
export interface DialogResult { value: string | null; data: Record<string, string> }

let layer: HTMLElement | null = null;
let current: { close: (v: string | null) => void } | null = null;

export function dialogOpen() { return current !== null; }

function formData(root: HTMLElement) {
  const data: Record<string, string> = {};
  for (const el of root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[name]')) {
    // A radio group answers with its checked option.
    if (el instanceof HTMLInputElement && el.type === 'radio' && !el.checked) continue;
    data[el.name] = el instanceof HTMLInputElement && el.type === 'checkbox' ? (el.checked ? '1' : '') : el.value;
  }
  return data;
}

export function openDialog(o: DialogOpts): Promise<DialogResult> {
  current?.close(null);
  if (!layer) { layer = document.createElement('div'); layer.id = 'dialogs'; document.body.appendChild(layer); }
  const before = document.activeElement as HTMLElement | null;
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  scrim.innerHTML = `<div class="dialog" role="dialog" aria-modal="true" aria-labelledby="dlg-title">
    <h3 id="dlg-title">${esc(o.title)}</h3>
    <div class="body">${o.body}</div>
    <div class="actions"><button class="btn" data-dlg="">Cancel</button>${o.actions.map((a) => `<button class="btn ${a.kind ?? ''}" data-dlg="${esc(a.value)}"${a.disabled ? ' disabled' : ''}>${esc(a.label)}</button>`).join('')}</div>
  </div>`;
  layer.appendChild(scrim);
  const box = scrim.firstElementChild as HTMLElement;
  return new Promise((resolve) => {
    const close = (value: string | null) => {
      const data = formData(box);
      document.removeEventListener('keydown', onKey, true);
      removeEventListener('resize', refit);
      scrim.remove();
      current = null;
      before?.focus?.();
      resolve({ value, data });
    };
    current = { close };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); close(null); return; }
      if (e.key === 'Enter' && !o.danger && !(e.target instanceof HTMLButtonElement) && !(e.target instanceof HTMLTextAreaElement)) {
        const confirm = [...box.querySelectorAll<HTMLButtonElement>('.actions button')].pop();
        if (confirm && !confirm.disabled && confirm.dataset.dlg) { e.preventDefault(); close(confirm.dataset.dlg); }
        return;
      }
      if (e.key === 'Tab') {
        const f = [...box.querySelectorAll<HTMLElement>('button:not(:disabled), input, select, textarea, a[href]')];
        if (!f.length) return;
        const i = f.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    // A long list in the dialog pages to fit it instead of scrolling (no scrolling anywhere, Cole 2026-10-27).
    const body = box.querySelector<HTMLElement>('.body')!;
    const refit = () => { if (scrim.isConnected) fitLists(body); };
    scrim.addEventListener('click', (e) => {
      if (e.target === scrim) return close(null);
      const go = (e.target as HTMLElement).closest<HTMLElement>('[data-fit-go]');
      if (go) { e.stopPropagation(); const [id, n] = go.dataset.fitGo!.split(':'); setFitPage(id, Number(n)); return refit(); }
      const b = (e.target as HTMLElement).closest('[data-dlg]') as HTMLButtonElement | null;
      if (b && !b.disabled) close(b.dataset.dlg || null);
    });
    if (o.onInput) {
      const run = () => { o.onInput!(box, formData(box)); refit(); };
      box.addEventListener('input', run);
      run();
    } else refit();
    addEventListener('resize', refit);
    const first = box.querySelector<HTMLElement>('.body input, .body select, .body textarea') ?? box.querySelector<HTMLElement>(o.danger ? '[data-dlg=""]' : '.actions button:last-child');
    first?.focus();
  });
}

// ---------------- toasts ----------------
// At most three, newest on top. An identical toast merges into the one showing
// ("Boss key dropped ×2"). Timers stop while the tab is hidden, so toasts don't
// all flash past (or pile up) when you come back.
interface Toast { el: HTMLElement; text: string; n: number; left: number; started: number; timer: number | null }
const toasts: Toast[] = [];
const MAX_TOASTS = 3;

function arm(t: Toast) {
  if (t.timer !== null || document.hidden) return;
  t.started = performance.now();
  t.timer = window.setTimeout(() => drop(t), t.left);
}
function disarm(t: Toast) {
  if (t.timer === null) return;
  clearTimeout(t.timer); t.timer = null;
  t.left = Math.max(500, t.left - (performance.now() - t.started));
}
function drop(t: Toast) {
  disarm(t);
  t.el.remove();
  const i = toasts.indexOf(t);
  if (i >= 0) toasts.splice(i, 1);
}
/** Drop every showing toast whose text passes `test` (a progress toast once its result is up). */
export function dropToasts(test: (text: string) => boolean) { for (const t of toasts.filter((x) => test(x.text))) drop(t); }
document.addEventListener('visibilitychange', () => { for (const t of toasts) document.hidden ? disarm(t) : arm(t); });

export function toast(msg: string, kind: '' | 'err' | 'gold' = '', action?: { label: string; run: () => void }, duration?: number) {
  const host = document.getElementById('toasts');
  if (!host) return;
  // Long errors ("Your gems are frozen... Contact support@") stay up long enough to read.
  const ms = duration ?? (kind === 'gold' ? 6000 : kind === 'err' ? Math.max(4000, msg.length * 70) : 4000);
  const same = toasts.find((t) => t.text === msg);
  // A toast with a button acts on its own moment (Undo), so it replaces an older copy instead of merging.
  if (same && action) drop(same);
  else if (same) {
    same.n++;
    same.el.querySelector('.n')!.textContent = ` ×${same.n}`;
    disarm(same); same.left = ms; arm(same);
    return;
  }
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.innerHTML = `<span>${esc(msg)}</span><span class="n"></span>${action ? `<button class="btn tiny">${esc(action.label)}</button>` : ''}`;
  const t: Toast = { el, text: msg, n: 1, left: ms, started: 0, timer: null };
  if (action) el.querySelector('button')!.addEventListener('click', () => { action.run(); drop(t); });
  host.prepend(el);
  toasts.unshift(t);
  while (toasts.length > MAX_TOASTS) drop(toasts[toasts.length - 1]);
  arm(t);
}
